import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { FC } from 'react';
import { useRoutesAndScheduleState } from '../../../contexts/routesAndSchedule.context';
import { useTranslation } from 'src/hooks/useTranslationHook';

const SelectedAssignmentDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedAssignment, routes, vehicles, drivers } =
    useRoutesAndScheduleState();

  if (!selectedAssignment) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="assignment-details-container"
      >
        <Typography>
          {translateText('administration.routesAndSchedule.noData')}
        </Typography>
      </Box>
    );
  }

  const assignedRoute = routes.find((r) => r.id === selectedAssignment.routeId);
  const assignedVehicle = vehicles.find(
    (v) => v.id === selectedAssignment.vehicleId
  );
  const assignedDriver = drivers.find(
    (d) => d.id === selectedAssignment.driverId
  );

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return translateText('common.nA');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString();
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="assignment-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText(
          'administration.routesAndSchedule.details.assignmentDetails'
        )}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.basicInfo'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="assignment-academic-year"
            >
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.academicYear'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedAssignment.academicYear}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="assignment-start-date"
            >
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.startDate'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedAssignment.startDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="assignment-end-date">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.endDate'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedAssignment.endDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="assignment-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.routesAndSchedule.columns.status'
                )}
              </Typography>
              <Chip
                label={
                  selectedAssignment.isActive
                    ? translateText(
                        'administration.routesAndSchedule.status.active'
                      )
                    : translateText(
                        'administration.routesAndSchedule.status.inactive'
                      )
                }
                color={selectedAssignment.isActive ? 'success' : 'default'}
                size="small"
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="assignment-students-count"
            >
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.studentsAssigned'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedAssignment.assignedStudents?.length || 0}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.routesAndSchedule.details.assignments'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="assignment-route">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.route'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignedRoute?.routeName ||
                  translateText(
                    'administration.routesAndSchedule.status.unknownRoute'
                  )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="assignment-vehicle">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.vehicle'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignedVehicle?.vehicleNumber ||
                  translateText(
                    'administration.routesAndSchedule.status.unknownVehicle'
                  )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="assignment-driver">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.routesAndSchedule.columns.driver'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignedDriver?.name ||
                  translateText(
                    'administration.routesAndSchedule.status.unknownDriver'
                  )}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SelectedAssignmentDetailsView;
